import CriteriaModal from '@/components/Base/modals/CandidateScreenCriteriaModal'
import { LoadCandidateScreeningTemplatesModal } from '@/components/Base/modals/LoadCandidateScreeningTemplatesModal'
import SaveAndLoadModal from '@/components/Base/modals/SaveLoadModal'
import { AccordionTitle } from '@/components/Shared/AccordionComponent'
import {
  API_DATE_TIME_FORMAT,
  DEFAULT_DATE_FORMAT,
} from '@/constants/scenarioBuilder.constant'
import {
  CriteriaOperation,
  GridView,
  SaveAndLoadModalAction,
} from '@/enums/portfolio-optimization.enum'
import { useAppSelector } from '@/hooks/use-app-selector'
import { useSaveLoadModal } from '@/hooks/useSaveLoadModal'
import {
  ICriteria,
  IScreeningCriterias,
  IScrreeningCriteriaPayload,
  ISecurity,
  ScreeningOption,
} from '@/interfaces/portfolioOptimization.interface'
import {
  getCandidateScreenSecurities,
  getCandidateScreeningItems,
  getCandidateScreeningTemplateDetails,
  getCandidateScreeningTemplates,
  getScreeningTemplatePayload,
  saveNewScreeningCriteria,
  updateScreeningCriteriaTemplate,
} from '@/services/portfolio-optimization/candidate-screening.service'
import {
  addCriteria,
  addScreeningCriteriaToList,
  resetSubsetScreening,
  setCriteriaList,
  setScreeningCriteriasList,
  setSubsetMode,
} from '@/states/candidateScreen.slice'
import { setScreeningItems } from '@/states/portfolioOptmization.slice'
import { showSnackbar } from '@/states/snackbar.slice'
import { SEMANTIC_COLOR_PALETTE } from '@/theme/theme-config'
import { getLatestDate } from '@/utils/utils'
import {
  AccordionActions,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import { ScreenCriteriaGrid } from '../CandidateScreens/CandidateScreenCriteriaGrid'
import { CandidateScreenSecuritiesGrid } from '../CandidateScreens/CandidateScreenSecuritiesGrid'

interface SubsetCandidateScreenProps {
  isSubset: boolean
  onClose: () => void
  onApply?: (criteriaName: string, criteriaData?: any) => void
  filterTo?: string // Filter parameter for screening items (S, E, P, V)
}
const SubsetCandidateScreen: React.FC<SubsetCandidateScreenProps> = ({
  isSubset,
  onClose,
  onApply,
  filterTo,
}) => {
  const dispatch = useDispatch()
  const selectedPortfolios = useAppSelector(
    (s) => s.portfolioOptimization.selectedPortfolios,
  )

  const candidateScreenState = useAppSelector((s) => s.candidateScreen)
  const [screenCriteriaModal, setScreenCriteriaModal] = useState(false)
  const [gridView, setGridView] = useState(GridView.ScreenCriteria)
  const [screeningOptions, setScreeningOptions] = useState<ScreeningOption[]>(
    [],
  )
  const [loadTemplateModal, setLoadTemplateModal] = useState(false)
  const [selectedScreenCriteriaState, setSelectedScreenCriteriaState] =
    useState<any>({})
  const [addedNewCriterias, setAddedNewCriterias] = useState<
    IScreeningCriterias[]
  >([])
  const [isNewScreenCriteriaAdded, setIsNewScreenCriteriaAdded] =
    useState<boolean>(false)
  const [selectedScreeningTemplateId, setSelectedScreeningTemplateId] =
    useState<string | null>(null)
  const [screeningTemplates, setScreeningTemplates] = useState<any[]>([])
  const [loadingScreenCriteriaDetails, setLoadingScreenCriteriaDetails] =
    useState(false)
  const [securities, setSecurities] = useState<ISecurity[]>([])
  const [securitiesFetching, setSecuritiesFetching] = useState(false)
  const [selectedPortfolioDate, setSelectedPortfolioDate] = useState<string>('')

  const CANDIDATE_SCREEN_CRITERIA_KEY = 'candidateScreenCriteria'
  const {
    screenConstraintName,
    screenConstraintNameList,
    updateScreenConstraintNameList,
    loading,
    openModal,
    setScreenConstraintName,
    handleModalOpen,
    handleModalClose,
    handleSave,
    handleLoad,
  } = useSaveLoadModal(CANDIDATE_SCREEN_CRITERIA_KEY)

  const handleViewTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGridView(event.target.value as GridView)
  }

  // Timestamp to prevent duplicate API calls within short time window
  const lastApiCallTime = React.useRef(0)

  useEffect(() => {
    dispatch(setSubsetMode(isSubset ?? false))
    const fetchScreeningCriteriaItems = async () => {
      try {
        const response = await getCandidateScreeningItems(filterTo)
        setScreeningOptions(response)
      } catch (err) {
        dispatch(
          showSnackbar({
            message: `Error fetching screening criteria items ${err instanceof Error ? err.message : String(err)}`,
            severity: 'error',
          }),
        )
      }
    }
    fetchScreeningCriteriaItems()

    // Cleanup function to reset subset mode when component unmounts or isSubset changes
    return () => {
      if (isSubset) {
        dispatch(setSubsetMode(false))
      }
    }
  }, [isSubset, filterTo])

  useEffect(() => {
    if (isNewScreenCriteriaAdded) {
      setAddedNewCriterias((prev) => [...prev, selectedScreenCriteriaState])
    }
  }, [isNewScreenCriteriaAdded])

  const createNewScreeningCriteria = async (
    payload: IScrreeningCriteriaPayload,
  ) => {
    delete payload.id
    try {
      const response = await saveNewScreeningCriteria(payload)
      setSelectedScreenCriteriaState({})
      setAddedNewCriterias([])
      loadSelectedTemplate(response.result)
      dispatch(
        showSnackbar({
          message: 'Screen criteria saved successfully for subset',
          severity: 'info',
        }),
      )
    } catch (err) {
      setScreenConstraintName('')
      dispatch(
        showSnackbar({
          message: `Error fetching while creating new screening criteria ${err instanceof Error ? err.message : String(err)}`,
          severity: 'error',
        }),
      )
    }
  }

  useEffect(() => {
    if (
      selectedPortfolios.length === 0 ||
      candidateScreenState.subsetScreeningCriteriaList.length === 0
    ) {
      setSecurities([])
      setSelectedPortfolioDate('')
      setSecuritiesFetching(false)
      return
    }

    const fetchCandidateScreenSecurities = async () => {
      try {
        let date = ''
        setSecurities([])
        setSelectedPortfolioDate('')
        if (
          selectedPortfolios.length &&
          candidateScreenState.subsetScreeningCriteriaList.length
        ) {
          setSecuritiesFetching(true)
          const portfolioDates = selectedPortfolios.map(
            (portfolioItem) => portfolioItem.date,
          )
          const latestDate = getLatestDate(portfolioDates)
          const portfolioCurrency = selectedPortfolios.find(
            (portfolio) => portfolio.date === latestDate,
          )?.currency
          date = dayjs(latestDate).format(API_DATE_TIME_FORMAT)
          setSelectedPortfolioDate(
            dayjs(latestDate).format(DEFAULT_DATE_FORMAT),
          )

          const payload = {
            screenCriteria: candidateScreenState.subsetScreeningCriteriaList,

            date: Number(date),
            currency: portfolioCurrency as string,
          }
          const response = await getCandidateScreenSecurities(payload)
          setSecurities(response)
          
          // Store securities globally for What-If Analysis access
          ;(window as any).__SCREENING_SECURITIES__ = response
        }
      } catch (err) {
        dispatch(
          showSnackbar({
            message: `Failed to load screening securities for constraints subset`,
            severity: 'error',
          }),
        )
        setSecurities([])
      } finally {
        setSecuritiesFetching(false)
      }
    }
    fetchCandidateScreenSecurities()
  }, [candidateScreenState.subsetScreeningCriteriaList, selectedPortfolios])

  const updateScreeningCriteria = async (
    payload: IScrreeningCriteriaPayload,
  ) => {
    const allCriterias: IScreeningCriterias[] = [
      ...candidateScreenState.screeningCriteriasList,
      ...payload.criterias,
    ]

    const reqBody: IScrreeningCriteriaPayload = {
      id: String(selectedScreeningTemplateId),
      name: screenConstraintName,
      criterias: allCriterias,
    }
    try {
      const response = await updateScreeningCriteriaTemplate(reqBody)
      setSelectedScreenCriteriaState({})
      setAddedNewCriterias([])
      loadSelectedTemplate(response.result)
      dispatch(
        showSnackbar({
          message: 'Screen criteria saved successfully',
          severity: 'info',
        }),
      )
    } catch (err) {
      setScreenConstraintName('')
      dispatch(
        showSnackbar({
          message: `Error fetching while creating new screening criteria ${err instanceof Error ? err.message : String(err)}`,
          severity: 'error',
        }),
      )
    }
  }

  const handleModalConfirm = () => {
    const storedConstraints = candidateScreenState.criteriaList

    if (openModal.mode === SaveAndLoadModalAction.SAVE) {
      const finalPayload = {
        name: screenConstraintName,
        criterias: [] as IScreeningCriterias[],
      }
      addedNewCriterias.forEach((newCriteriaObj) => {
        const payload = getScreeningTemplatePayload(
          newCriteriaObj,
          screenConstraintName,
        ) as IScrreeningCriteriaPayload
        finalPayload.criterias.push(payload.criterias[0])
      })
      if (selectedScreeningTemplateId) {
        updateScreeningCriteria(finalPayload as IScrreeningCriteriaPayload)
      } else {
        createNewScreeningCriteria(finalPayload as IScrreeningCriteriaPayload)
      }
    } else if (openModal.mode === SaveAndLoadModalAction.LOAD) {
      const savedData = handleLoad(storedConstraints)
      if (savedData) {
        dispatch(setCriteriaList(savedData))
      }
    }

    handleModalClose()
  }

  const handleScreenCriteriaModalClose = () => {
    setScreenCriteriaModal(false)
  }

  const handleScreenCriteriaModalSave = (
    rawCriteria: IScreeningCriterias,
    computedCriteria: string,
  ) => {
    const newCriteria: ICriteria = {
      id: rawCriteria.id,
      noOfSecurities: null,
      screenCriteria: computedCriteria,
    }
    dispatch(addScreeningCriteriaToList(rawCriteria))
    dispatch(addCriteria(newCriteria))

    // close modal first
    handleScreenCriteriaModalClose()
  }

  const fetchCandidateScreeningTemplates = async () => {
    try {
      const res = await getCandidateScreeningTemplates()
      setScreeningTemplates(res)
    } catch (error) {
      dispatch(
        showSnackbar({
          message: `Error fetching candidate screening templates ${error instanceof Error ? error.message : String(error)}`,
          severity: 'error',
        }),
      )
    }
  }

  const loadSelectedTemplate = async (templateId: string) => {
    setLoadingScreenCriteriaDetails(true)
    if (!templateId || templateId === '') {
      dispatch(
        showSnackbar({
          message: 'Please select a template',
          severity: 'error',
        }),
      )
      setLoadingScreenCriteriaDetails(false)
      return
    }

    try {
      const res = await getCandidateScreeningTemplateDetails(templateId)
      const screeningsWithRowKeys =
        res.criterias?.map((s) => {
          const rowKey = s.rowKey || uuidv4()
          return {
            ...s,
            rowKey,
          }
        }) || []
      setSelectedScreeningTemplateId(res?.id || '')
      dispatch(setScreeningCriteriasList(screeningsWithRowKeys))
      const criteriaList = screeningsWithRowKeys.map(
        (criteria: IScreeningCriterias) => ({
          id: criteria.id,
          screenCriteria: criteria?.data.axiomScreenItemData?.title || '',
          noOfSecurities: criteria?.data?.configSetting?.noOfSecurities || 0,
          rowKey: criteria.rowKey,
        }),
      )
      dispatch(setCriteriaList(criteriaList || []))
      setLoadTemplateModal(false)
      setScreenConstraintName(res?.name || '')
    } catch (error) {
      dispatch(
        showSnackbar({
          message: `Error loading candidate screening template ${error instanceof Error ? error.message : String(error)}`,
          severity: 'error',
        }),
      )
    } finally {
      setLoadingScreenCriteriaDetails(false)
    }
  }

  useEffect(() => {
    if (loadTemplateModal) {
      fetchCandidateScreeningTemplates()
    }
  }, [loadTemplateModal])

  return (
    <Accordion expanded={true} className='subset-candidate-screen'>
      <AccordionTitle aria-controls='panel3-content' id='panel3-header'>
        <Typography variant='h3'>Candidate Screening</Typography>
      </AccordionTitle>
      <AccordionDetails
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          padding: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box paddingLeft={1}>
            <FormControl>
              <RadioGroup
                row
                aria-labelledby='candidate-screen-radio-buttons-group'
                name='candidate-screen-radio-buttons'
                value={gridView}
                onChange={handleViewTypeChange}
              >
                <FormControlLabel
                  value={GridView.ScreenCriteria}
                  control={<Radio />}
                  label='Screen Criteria'
                />
                <FormControlLabel
                  value={GridView.Securities}
                  control={<Radio />}
                  label='Securities'
                />
              </RadioGroup>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              className='add-screen-criteria-button'
              size='small'
              variant='outlined'
              onClick={() => {
                setScreenCriteriaModal(true)
              }}
            >
              Add New Screen Criteria
            </Button>
            <Button
              size='small'
              variant='outlined'
              data-testid='load-screen-criteria-button'
              onClick={() => setLoadTemplateModal(true)}
            >
              Load Screen Criteria
            </Button>
            <Button
              size='small'
              variant='outlined'
              onClick={() => handleModalOpen(SaveAndLoadModalAction.SAVE)}
            >
              Save Current Screen Criteria
            </Button>
          </Box>
        </Box>
        {gridView === GridView.ScreenCriteria && (
          <Box sx={{ position: 'relative', minHeight: '300px' }}>
            {securitiesFetching ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '300px',
                  gap: 2,
                }}
              >
                <CircularProgress size={40} />
                <Typography variant="body2" color="text.secondary">
                  Loading securities based on screening criteria...
                </Typography>
              </Box>
            ) : (
              <ScreenCriteriaGrid
                criteriaList={candidateScreenState.subsetCriteriaList}
              />
            )}
          </Box>
        )}
        {gridView === GridView.Securities && (
          <CandidateScreenSecuritiesGrid
            securitiesList={securities}
            securitiesFetching={securitiesFetching}
            selectedPortfolioDate={selectedPortfolioDate}
          />
        )}
      </AccordionDetails>
      <AccordionActions
        sx={{
          padding: 2,
          borderTop: '1px solid',
          borderTopColor: SEMANTIC_COLOR_PALETTE.border.light,
        }}
      >
        <Button
          size='small'
          variant='text'
          onClick={() => {
            dispatch(resetSubsetScreening())
          }}
          disabled={!candidateScreenState.subsetScreeningCriteriaList.length}
        >
          Reset
        </Button>
        <Button
          size='small'
          variant='contained'
          disabled={!candidateScreenState.subsetCriteriaList?.length || securitiesFetching}
          onClick={async () => {
            if (isSubset === true) {
              if (onApply) {
                try {
                  const axiosInstance = (await import('@/utils/interceptor')).default
                  // Prepare the payload - use the full screening criteria list
                  const payload = candidateScreenState.subsetScreeningCriteriaList || []
                  const response = await axiosInstance.post(
                    '/screening-templates/computeAddSubTitle?filterTo=OSPEV',
                    payload
                  )
                  
                  if (response.data.success) {
                    // Get screen criteria name and data from API response
                    const screenCriteriaName = response.data.result 
                    // Pass single object with screenCriterias property
                    const allCriteriaData = candidateScreenState.subsetScreeningCriteriaList || []
                    const screenCriteriaData = {
                      screenCriterias: allCriteriaData.map(criteria => ({
                        id: criteria.id,
                        data: {
                          selectedData: criteria.data?.selectedData || {},
                          item: criteria.data?.itemType || criteria.id,
                          itemType: criteria.data?.itemType,
                          configSetting: criteria.data?.configSetting || { isInclude: true }
                        }
                      }))
                    }
                    // Pass both name and data from API response to the callback
                    onApply(screenCriteriaName, screenCriteriaData)
                    onClose()
                  } else {
                    dispatch(resetSubsetScreening())
                    return
                  }
                } catch (error) {
                  dispatch(resetSubsetScreening())
                  return
                }
                dispatch(resetSubsetScreening())
              } else {
                onClose()
                dispatch(
                  setScreeningItems(
                    candidateScreenState.subsetScreeningCriteriaList,
                  ),
                )
                // Reset the modal state after applying (same as scenario builder)
                dispatch(resetSubsetScreening())
              }
            }
          }}
        >
          Apply Candidate Screen
        </Button>
        <LoadCandidateScreeningTemplatesModal
          loading={loadingScreenCriteriaDetails}
          loadTemplateModal={loadTemplateModal}
          setLoadTemplateModal={setLoadTemplateModal}
          screeningTemplates={screeningTemplates}
          loadSelectedTemplate={loadSelectedTemplate}
          selectedScreeningTemplateId={selectedScreeningTemplateId}
          setSelectedScreeningTemplateId={setSelectedScreeningTemplateId}
        />

        {/* Configurable Modal */}
        <SaveAndLoadModal
          isOpen={openModal.isOpen}
          onClose={handleModalClose}
          mode={openModal.mode}
          currentValue={screenConstraintName}
          options={screenConstraintNameList}
          onChange={setScreenConstraintName}
          onConfirm={handleModalConfirm}
          loading={loading}
          saveTitle='Save New Screen Criteria'
          loadTitle='Load Screen Criteria'
          savePlaceholder='Type criteria name to save'
          loadPlaceholder='Search criteria name..'
          saveButtonText='Save'
          loadButtonText='Load'
        />

        {screenCriteriaModal && (
          <CriteriaModal
            screeningOptions={screeningOptions}
            defaultScreeningOption={screeningOptions[0]}
            mode={CriteriaOperation.ADD}
            isOpen={screenCriteriaModal}
            onSubmit={handleScreenCriteriaModalSave}
            onClose={handleScreenCriteriaModalClose}
            setIsNewScreenCriteriaAdded={setIsNewScreenCriteriaAdded}
            setSelectedScreenCriteriaState={(value) => {
              setSelectedScreenCriteriaState(value)
            }}
          />
        )}
      </AccordionActions>
    </Accordion>
  )
}

export default React.memo(SubsetCandidateScreen)
